import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Button } from "react-bootstrap";
import "swiper/css";
import "swiper/css/free-mode";
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  deleteDoc,
  addDoc,
  writeBatch,
  deleteField,
} from "firebase/firestore";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import { useAlert } from "react-alert";
import getIcon from "../../../utility/getIcon";

import { db } from "../../../firebase";
import { ReactComponent as All } from "../../../images/All.svg";
import { ReactComponent as Plus } from "../../../images/Plus.svg";
import { ReactComponent as Beer } from "../../../images/Beers.svg";
import { ReactComponent as Softs } from "../../../images/Softs.svg";
import { ReactComponent as Food } from "../../../images/Fast-food.svg";
import { ReactComponent as HotDrinks } from "../../../images/hotDrinks.svg";
import { ReactComponent as Cocktails } from "../../../images/Cocktails.svg";
import { ReactComponent as Wine } from "../../../images/Wine.svg";
import { ReactComponent as Others } from "../../../images/Others.svg";
import { CategoriesArray } from "../../../utility/data";
import "./pos.scss";
import { Loader2 } from "../../../helper/loader";
import AddProduct from "../../../components/AddProducts/AddProduct";
import AddCategory from "../../../components/AddProducts/AddCategory";
import AddOptionProduct from "../../../components/AddProducts/AddOptionProduct";
import AddOptionCategory from "../../../components/AddProducts/AddOptionCategory";
import ManageOrg from "../../../components/ManageOrg/ManageOrg";
import EmptyState from "../../../components/emptyState/EmptyState";
import * as XLSX from "xlsx";
import importPosProducts from "../../../utility/importPosProducts";
import * as Yup from "yup";
import OutlinedPinkButton from "../../../components/buttons/outlinedPinkButton/OutlinedPinkButton";
import CategoryFilter from "../../../components/CategoryFilter/CategoryFilter";
import AiMenuImport from "../../../components/AddProducts/AiMenuImport";
import { generateProductIcon, regenerateAllIcons, retryMenuIcons } from "../../../utility/aiApi";
import downloadPosMenuPDF from "../../../utility/downloadPosMenuPDF";

const getOrderValue = (product, orderField = "productOrder") =>
  Number.isFinite(Number(product?.[orderField]))
    ? Number(product[orderField])
    : Number.MAX_SAFE_INTEGER;

const sortProductsForDisplay = (items, orderField = "productOrder") =>
  [...items].sort((a, b) => {
    const orderDiff = getOrderValue(a, orderField) - getOrderValue(b, orderField);
    if (orderDiff !== 0) return orderDiff;

    const priceDiff = Number(a.price || 0) - Number(b.price || 0);
    if (priceDiff !== 0) return priceDiff;

    return (a.title || "").localeCompare(b.title || "");
  });

function SortableProductCard({ item, selectedOptionCategory, onOpenProduct, t }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });
  const fallbackIcon = getIcon(item?.category, item?.customIcon);
  const [imageSrc, setImageSrc] = useState(item?.imageUrl || fallbackIcon);
  const isGeneratedImage = Boolean(item?.imageUrl && imageSrc === item.imageUrl);

  useEffect(() => {
    setImageSrc(item?.imageUrl || fallbackIcon);
  }, [item?.imageUrl, fallbackIcon]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <button
      ref={setNodeRef}
      style={style}
      className={`productCard ${isDragging ? "is-dragging" : ""}`}
      onClick={onOpenProduct}
      {...attributes}
      {...listeners}
    >
      <div className="productImg">
        <img
          className={isGeneratedImage ? "generated-product-icon" : ""}
          src={imageSrc}
          alt=""
          onError={() => {
            if (imageSrc !== fallbackIcon) setImageSrc(fallbackIcon);
          }}
        />
      </div>
      <div className="productDetails">
        <h5>
          {item.title} {item.servingSize ? "(" + item.servingSize + " mL)" : ""}
        </h5>
        {item.outOfStock ? (
          <p className="outOfStock">{t("out_of_stock")}</p>
        ) : (
          <div className="horizontal">
            {!selectedOptionCategory && Number(item?.ticketNumber) !== 0 && item?.ticketColor && (
              <div className="ticket-info">
                <p className="ticket-number">{item.ticketNumber}</p>
                <div
                  className="color-circle"
                  style={{
                    backgroundColor: item.ticketColor,
                  }}
                ></div>
              </div>
            )}
            {!selectedOptionCategory &&
              Number(item?.secondTicketNumber) !== 0 &&
              item?.secondTicketColor && (
                <div className="ticket-info">
                  <p className="ticket-number">{item.secondTicketNumber}</p>
                  <div
                    className="color-circle"
                    style={{
                      backgroundColor: item.secondTicketColor,
                    }}
                  ></div>
                </div>
              )}
            <p className="price">{item.price}€</p>
          </div>
        )}
      </div>
    </button>
  );
}

function POS({ organizations, orgId }) {
  const { t, i18n } = useTranslation("editpos");
  const alert = useAlert();
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [addNew, setAddNew] = useState(false);
  const [loader, setLoader] = useState(false);
  const [products, setProducts] = useState([]);
  const [customCategories, setCustomCategories] = useState([]);
  const [optionCategories, setOptionCategories] = useState([]);
  const [editingOptionCategory, setEditingOptionCategory] = useState(null);
  const [optionProducts, setOptionProducts] = useState([]);
  const [selectedOptionCategory, setSelectedOptionCategory] = useState(null);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState("");
  const [showDelete, setShowDelete] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState();
  const fileInputRef = useRef(null);
  const optionFileInputRef = useRef(null);
  const userStore = useSelector((state) => state.userReducer);
  const [showAlert, setShowAlert] = useState(false);
  const [showOptionAlert, setShowOptionAlert] = useState(false);
  const [errorText, setErrorText] = useState(null);
  const productImportRunning = useRef(false);
  const [isManageOrgOpen, setIsManageOrgOpen] = useState(false);
  const [showAiMenuImport, setShowAiMenuImport] = useState(false);
  const [regeneratingAllIcons, setRegeneratingAllIcons] = useState(false);
  const [exportingMenuPdf, setExportingMenuPdf] = useState(false);
  const [showDisableStockConfirmation, setShowDisableStockConfirmation] = useState(false);
  const [disablingStock, setDisablingStock] = useState(false);
  const managementSectionRef = useRef(null);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const baseCategories = ["Beers", "Softs", "Food", "Cocktails", "Wine", "Hot drinks", "Other"];
  const currentOrgId = orgId?.[0];
  const currentOrg = organizations.find((org) => org.id === currentOrgId);
  const pendingAiIcons = [...products, ...optionProducts].filter(
    (product) => product.aiIconStatus === "pending",
  ).length;
  const failedAiIcons = [
    ...products.map((product) => ({ ...product, collectionName: "products" })),
    ...optionProducts.map((product) => ({ ...product, collectionName: "OptionProducts" })),
  ].filter((product) => product.aiIconStatus === "failed");

  useEffect(() => {
    if (!pendingAiIcons) return undefined;
    const interval = window.setInterval(() => {
      getOrgProduct();
      getOptionProducts();
    }, 10000);
    return () => window.clearInterval(interval);
  }, [pendingAiIcons, currentOrgId, selectedCategory, selectedOptionCategory]);

  const retryFailedAiIcons = async () => {
    const importIds = [
      ...new Set(failedAiIcons.map((product) => product.aiImportId).filter(Boolean)),
    ];
    try {
      await Promise.all(importIds.map(retryMenuIcons));
      await Promise.all(
        failedAiIcons
          .filter((product) => !product.aiImportId)
          .map((product) =>
            generateProductIcon({
              orgId: currentOrgId,
              productId: product.id,
              collectionName: product.collectionName,
            }),
          ),
      );
      await getOrgProduct();
      await getOptionProducts();
      alert.show(t("ai_icons_retried"), { type: "success" });
    } catch (error) {
      alert.show(error.message, { type: "error" });
    }
  };

  const regenerateAllAiIcons = async () => {
    if (!currentOrgId || regeneratingAllIcons) return;
    setRegeneratingAllIcons(true);
    try {
      const result = await regenerateAllIcons(currentOrgId);
      await Promise.all([getOrgProduct(), getOptionProducts()]);
      alert.show(t("ai_icons_regenerate_all_started", { count: result.count }), {
        type: "success",
      });
    } catch (error) {
      alert.show(error.message, { type: "error" });
    } finally {
      setRegeneratingAllIcons(false);
    }
  };

  const excelHeaders = [
    "title",
    "category",
    "price",
    "vat",
    "vatOut",
    "availableQuantity",
    "outOfStock",
    "customIcon",
    "optionCategories",
    "ticketNumber",
    "ticketColor",
    "secondTicketNumber",
    "secondTicketColor",
    "stockItemId",
    "quantityPerSale",
    "outOfStockBehavior",
  ];

  const optionExcelHeaders = [
    "title",
    "category",
    "price",
    "vat",
    "vatOut",
    "availableQuantity",
    "outOfStock",
    "customIcon",
    "stockItemId",
    "quantityPerSale",
    "outOfStockBehavior",
  ];

  const getOrgCategories = async () => {
    if (!currentOrgId) {
      setCustomCategories([]);
      return;
    }

    let records = [];
    const q = query(collection(db, "Categories"), where("orgId", "==", currentOrgId));
    const querySnapshot = await getDocs(q);
    querySnapshot.forEach((doc) => records.push(doc.data()));
    setCustomCategories(records);
  };

  const getOptionCategories = async () => {
    if (!currentOrgId) {
      setOptionCategories([]);
      return;
    }

    let records = [];
    const q = query(collection(db, "OptionCategories"), where("orgId", "==", currentOrgId));
    const querySnapshot = await getDocs(q);
    querySnapshot.forEach((doc) => records.push(doc.data()));
    setOptionCategories(records);
  };

  const getOptionProducts = async () => {
    if (!currentOrgId) {
      setOptionProducts([]);
      setFilteredProducts([]);
      return;
    }

    let records = [];
    const q = query(collection(db, "OptionProducts"), where("orgId", "==", currentOrgId));
    const querySnapshot = await getDocs(q);
    querySnapshot.forEach((doc) => records.push({ id: doc.id, ...doc.data() }));
    records = sortProductsForDisplay(records, "optionOrder");
    setOptionProducts(records);
    if (selectedOptionCategory) {
      records = records.filter((record) => record.category === selectedOptionCategory);
    }
    setFilteredProducts(records);
  };

  const openOptionProductForm = (product = undefined) => {
    if (optionCategories.length === 0) {
      alert.show(t("create_option_category_first"), { type: "error" });
      return;
    }

    setSelectedProduct(product);
    setAddNew("optionProduct");
  };

  const getOrgProduct = async () => {
    if (!currentOrgId) {
      setProducts([]);
      setFilteredProducts([]);
      return;
    }

    let records = [];
    const q = query(collection(db, "products"), where("orgId", "==", currentOrgId));
    const querySnapshot = await getDocs(q);
    querySnapshot.forEach((doc) => records.push({ id: doc.id, ...doc.data() }));
    records = sortProductsForDisplay(records);
    setProducts(records);

    if (!selectedOptionCategory) {
      if (selectedCategory !== "All") {
        records = records.filter((record) => record.category === selectedCategory);
      }
      setFilteredProducts(records);
    }
  };

  const deleteProduct = async () => {
    setLoader(true);
    try {
      const collectionName = selectedOptionCategory ? "OptionProducts" : "products";
      const productRef = doc(db, collectionName, selectedProduct.id);
      await deleteDoc(productRef);
      if (selectedOptionCategory) {
        getOptionProducts();
      } else {
        getOrgProduct();
      }
      setSelectedProduct(null);
    } catch (error) {
      console.error("Error deleting product: ", error);
    }
    setLoader(false);
    setShowDelete(false);
  };

  const handleDeleteCategory = async (id, title) => {
    setLoader(true);
    try {
      // Check if any product has this category title
      const hasProducts = products.some((doc) => doc.category === title);

      if (hasProducts) {
        setErrorText(t("cannot_delete_category"));
        setLoader(false);
        setShowDelete(false);
        return;
      }

      const categoryRef = doc(db, "Categories", id);
      await deleteDoc(categoryRef);
      getOrgCategories();
      setSelectedCategory(null);
    } catch (error) {
      console.error("Error deleting category: ", error);
    }
    setLoader(false);
    setShowDelete(false);
  };

  const handleDeleteOptionCategory = async (id, title) => {
    setLoader(true);
    try {
      const hasProducts = optionProducts.some((doc) => doc.category === title);
      if (hasProducts) {
        setErrorText(t("cannot_delete_category"));
        setLoader(false);
        return;
      }
      const categoryRef = doc(db, "OptionCategories", id);
      await deleteDoc(categoryRef);
      await getOptionCategories();
      setSelectedOptionCategory(null);
      if (addNew === "optionProduct") {
        setAddNew(false);
        setSelectedProduct(null);
      }
    } catch (error) {
      console.error("Error deleting option category: ", error);
    }
    setLoader(false);
  };

  useEffect(() => {
    if (!currentOrgId) {
      setSelectedOrg("");
      setProducts([]);
      setCustomCategories([]);
      setOptionCategories([]);
      setOptionProducts([]);
      setFilteredProducts([]);
      return;
    }

    setSelectedOrg(organizations.find((orga) => orga.id === currentOrgId));
    getOrgCategories();
    getOptionCategories();
    getOptionProducts();
    getOrgProduct();
  }, [currentOrgId, organizations, addNew, selectedProduct]);

  useEffect(() => {
    setSelectedOptionCategory(null);
    setEditingOptionCategory(null);
    setAddNew(false);
  }, [currentOrgId]);

  useEffect(() => {
    if (addNew) {
      setIsManageOrgOpen(true);
    }
  }, [addNew]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!(isManageOrgOpen || addNew)) return;
    if (!window.matchMedia("(max-width: 768px)").matches) return;

    const timeoutId = window.setTimeout(() => {
      managementSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 120);

    return () => window.clearTimeout(timeoutId);
  }, [isManageOrgOpen, addNew, selectedProduct]);

  //FILTER CATEGORY OR OPTION CATEGORY
  useEffect(() => {
    if (selectedOptionCategory) {
      const optionFiltered = sortProductsForDisplay(
        optionProducts.filter((product) => product.category === selectedOptionCategory),
        "optionOrder",
      );
      setFilteredProducts(optionFiltered);
    } else if (selectedCategory === "All") {
      setFilteredProducts(products);
    } else {
      const newFilterProducts = products.filter((product) => product.category === selectedCategory);
      setFilteredProducts(newFilterProducts);
    }
  }, [selectedCategory, selectedOptionCategory, products, optionProducts]);

  function exportToCsv() {
    const formattedProducts = Object.values(filteredProducts).map((item) => {
      const base = {
        title: item.title,
        category: item.category,
        price: item.price,
        vat: item.vat ?? "",
        vatOut: item.vatOut ?? "",
        availableQuantity: item.availableQuantity,
        outOfStock: item.outOfStock ? "Yes" : "No",
        customIcon: item.customIcon ?? "",
        optionCategories: Array.isArray(item.optionCategories)
          ? item.optionCategories.join(",")
          : "",
        ticketNumber: item.ticketNumber ?? 0,
        ticketColor: item.ticketColor ?? "",
        secondTicketNumber: item.secondTicketNumber ?? 0,
        secondTicketColor: item.secondTicketColor ?? "",
        stockItemId: item.stockItemId ?? "",
        quantityPerSale: item.quantityPerSale ?? "",
        outOfStockBehavior: item.outOfStockBehavior ?? "",
      };

      return base;
    });

    // Création de la feuille pour sérialisation CSV
    const worksheet = XLSX.utils.json_to_sheet([]);
    XLSX.utils.sheet_add_aoa(worksheet, [excelHeaders], { origin: "A1" });
    XLSX.utils.sheet_add_json(worksheet, formattedProducts, {
      origin: "A2",
      skipHeader: true,
    });

    // Largeur des colonnes
    worksheet["!cols"] = excelHeaders.map(() => ({ wch: 25 }));

    // Export CSV UTF-8
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Products");
    XLSX.writeFile(workbook, "Products.csv", { bookType: "csv" });
  }

  const disableOrganizationStockTracking = async () => {
    if (!currentOrgId || disablingStock) return;

    setDisablingStock(true);
    try {
      const [productSnapshot, optionProductSnapshot] = await Promise.all([
        getDocs(query(collection(db, "products"), where("orgId", "==", currentOrgId))),
        getDocs(query(collection(db, "OptionProducts"), where("orgId", "==", currentOrgId))),
      ]);
      const productDocs = [...productSnapshot.docs, ...optionProductSnapshot.docs];

      // `availableQuantity: false` represents unlimited legacy stock. Clearing
      // both modern and legacy links keeps the mobile POS from blocking sales.
      for (let start = 0; start < productDocs.length; start += 450) {
        const batch = writeBatch(db);
        productDocs.slice(start, start + 450).forEach((productDoc) => {
          batch.update(productDoc.ref, {
            availableQuantity: false,
            outOfStock: false,
            stockLinks: [],
            stockItemId: deleteField(),
            quantityPerSale: deleteField(),
            outOfStockBehavior: deleteField(),
            onHand: deleteField(),
          });
        });
        await batch.commit();
      }

      await Promise.all([getOrgProduct(), getOptionProducts()]);
      alert.show(t("stock_tracking_disabled_success", { count: productDocs.length }), {
        type: "success",
      });
      setShowDisableStockConfirmation(false);
    } catch (error) {
      console.error("Unable to disable stock tracking:", error);
      alert.show(t("stock_tracking_disabled_error"), { type: "error" });
    } finally {
      setDisablingStock(false);
    }
  };

  const exportMenuPdf = async () => {
    if (!products.length || exportingMenuPdf) return;
    setExportingMenuPdf(true);
    try {
      await downloadPosMenuPDF({
        organization: selectedOrg || currentOrg,
        products,
        customCategories,
        categoryLabels: {
          Beers: t("beers"),
          Softs: t("softs"),
          Food: t("food"),
          Cocktails: t("cocktails"),
          Wine: t("wine"),
          "Hot drinks": t("hot_drinks"),
          Other: t("others"),
        },
        labels: {
          title: t("menu_pdf_title"),
          generatedAt: t("menu_pdf_generated_at"),
          uncategorized: t("menu_pdf_uncategorized"),
          empty: t("prod_empty_title"),
        },
        locale: i18n.language || "fr",
      });
    } catch (error) {
      alert.show(error.message || t("menu_pdf_export_error"), { type: "error" });
    } finally {
      setExportingMenuPdf(false);
    }
  };

  // Verification to do while transforming data
  function transformDataOnImport(data, colNames) {
    // Ignore empty spreadsheet rows while retaining their original line numbers.
    return data.slice(1).map((item, index) => ({ item, lineNumber: index + 2 }))
      .filter(({ item }) => item.some((value) => value != null && String(value).trim() !== ""))
      .map(({ item, lineNumber }) => {
      const line = {};
      Object.defineProperty(line, "__importRow", { value: lineNumber });
      colNames.forEach((header, index) => (line[header] = item[index]));
      line.outOfStock = line.outOfStock === "Yes";
      if (line.optionCategories) {
        line.optionCategories = line.optionCategories
          .split(",")
          .map((i) => i.trim())
          .filter((i) => i);
      }
      line.stockItemId = line.stockItemId || null;
      line.quantityPerSale =
        line.quantityPerSale || line.quantityPerSale === 0 ? Number(line.quantityPerSale) : null;
      line.outOfStockBehavior = line.outOfStockBehavior || null;

      return line;
    });
  }

  async function handleAddOptionProduct(product) {
    const productData = {
      title: product?.title,
      price: product?.price,
      vat: product?.vat,
      vatOut: product?.vatOut,
      customIcon: product?.customIcon,
      category: product?.category,
      ticketColor: product?.ticketColor || null,
      ticketNumber: product?.ticketNumber || 0,
      secondTicketColor: product?.secondTicketColor || null,
      secondTicketNumber: product?.secondTicketNumber || 0,
      uid: userStore?.users.uid || null,
      orgId: currentOrgId,
      optionOrder: optionProducts.filter((item) => item.category === product?.category).length,
      outOfStock: product?.outOfStock || null,
      availableQuantity: product?.availableQuantity || false,
      stockItemId: product?.stockItemId || null,
      quantityPerSale: product?.quantityPerSale || null,
      outOfStockBehavior: product?.outOfStockBehavior || null,
    };
    try {
      await addDoc(collection(db, "OptionProducts"), productData);
    } catch (error) {
      console.error(`${product?.title} hasn't been added !!`, error);
    }
  }

  async function handleUpdateOptionFunction(product, id) {
    try {
      await updateDoc(doc(db, "OptionProducts", id), {
        price: product?.price,
        vat: product?.vat,
        vatOut: product?.vatOut,
        availableQuantity: product?.availableQuantity || 0,
        outOfStock: product?.outOfStock || null,
        ticketColor: product?.ticketColor || null,
        ticketNumber: product?.ticketNumber || 0,
        secondTicketColor: product?.secondTicketColor || null,
        secondTicketNumber: product?.secondTicketNumber || 0,
        customIcon: product?.customIcon,
        stockItemId: product?.stockItemId || null,
        quantityPerSale: product?.quantityPerSale || null,
        outOfStockBehavior: product?.outOfStockBehavior || null,
      });
    } catch (error) {
      console.log(`${id} has not been updated !!!`);
    }
  }

  const handleOpenProductForm = (item) => {
    setSelectedProduct(item);
    setAddNew("product");
  };

  const handleReorderProducts = async ({ active, over }) => {
    if (!over || active.id === over.id || selectedOptionCategory) return;

    const visibleProducts = sortProductsForDisplay(filteredProducts);
    const oldIndex = visibleProducts.findIndex((product) => product.id === active.id);
    const newIndex = visibleProducts.findIndex((product) => product.id === over.id);

    if (oldIndex < 0 || newIndex < 0) return;

    const reorderedVisible = arrayMove(visibleProducts, oldIndex, newIndex);
    const visibleIds = new Set(visibleProducts.map((product) => product.id));
    const reorderedVisibleQueue = [...reorderedVisible];
    const orderedProducts = sortProductsForDisplay(products);

    const reorderedProducts =
      selectedCategory === "All"
        ? reorderedVisible
        : orderedProducts.map((product) =>
            visibleIds.has(product.id) ? reorderedVisibleQueue.shift() : product,
          );

    const productsWithOrder = reorderedProducts.map((product, index) => ({
      ...product,
      productOrder: index,
    }));

    setProducts(productsWithOrder);
    setFilteredProducts(
      selectedCategory === "All"
        ? productsWithOrder
        : productsWithOrder.filter((product) => product.category === selectedCategory),
    );

    try {
      const batch = writeBatch(db);
      productsWithOrder.forEach((product) => {
        batch.update(doc(db, "products", product.id), {
          productOrder: product.productOrder,
        });
      });
      await batch.commit();
    } catch (error) {
      console.error("Error updating products order: ", error);
      getOrgProduct();
    }
  };

  const handleReorderOptionProducts = async ({ active, over }) => {
    if (!over || active.id === over.id || !selectedOptionCategory) return;

    const visibleOptions = sortProductsForDisplay(filteredProducts, "optionOrder");
    const oldIndex = visibleOptions.findIndex((product) => product.id === active.id);
    const newIndex = visibleOptions.findIndex((product) => product.id === over.id);

    if (oldIndex < 0 || newIndex < 0) return;

    const reorderedOptions = arrayMove(visibleOptions, oldIndex, newIndex).map(
      (product, index) => ({
        ...product,
        optionOrder: index,
      }),
    );

    const reorderedOptionIds = new Set(reorderedOptions.map((product) => product.id));
    const nextOptionProducts = optionProducts.map((product) => {
      if (!reorderedOptionIds.has(product.id)) return product;
      return reorderedOptions.find((item) => item.id === product.id);
    });

    setOptionProducts(nextOptionProducts);
    setFilteredProducts(reorderedOptions);

    try {
      const batch = writeBatch(db);
      reorderedOptions.forEach((product) => {
        batch.update(doc(db, "OptionProducts", product.id), {
          optionOrder: product.optionOrder,
        });
      });
      await batch.commit();
    } catch (error) {
      console.error("Error updating option products order: ", error);
      getOptionProducts();
    }
  };

  async function handleDeleteOptionProduct(id) {
    try {
      const productRef = doc(db, "OptionProducts", id);
      await deleteDoc(productRef);
    } catch (error) {
      console.error("Error deleting option product: ", error);
    }
  }

  // =========== Start of utils for validations ===========
  const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
  const percentageTest = (value) => {
    if (!value && value !== 0) return true;
    const num = Number(value);
    return !isNaN(num) && num >= 0 && num <= 100 && /^\d+(\.\d+)?$/.test(value);
  };

  const ticketNumberTransform = (value, originalValue) => {
    if (originalValue === "") return null;
    const num = Number(originalValue);
    return isNaN(num) ? originalValue : num;
  };
  // =========== End of utils for validations ===========

  // =========== Start of validations for product(s) ===========
  const productSchema = Yup.object().shape({
    title: Yup.string()
      .required("Title is required")
      .test("not-empty", "Title cannot be empty", (value) => value?.trim() !== ""),

    price: Yup.string()
      .required("Price is required")
      .test("not-empty", "Price cannot be empty", (value) => value?.trim() !== "")
      .matches(/^-?\d*(\.\d+)?$/, "Price must be a valid number"),

    category: Yup.string()
      .required("Category is required")
      .test("not-empty", "Category cannot be empty", (value) => value?.trim() !== "")
      .test("category-exists", "Category does not exist", function (value) {
        const { customCategories, baseCategories } = this.options.context;
        return customCategories.some((c) => c.title === value) || baseCategories.includes(value);
      }),

    outOfStock: Yup.boolean().required("Out of stock is required"),

    ticketNumber: Yup.number()
      .transform(ticketNumberTransform)
      .nullable()
      .test(
        "is-valid-ticket-number",
        "Ticket number must be a number, null, or -1",
        (value) => value === null || typeof value === "number",
      ),

    secondTicketNumber: Yup.number()
      .transform(ticketNumberTransform)
      .nullable()
      .test(
        "is-valid-ticket-number",
        "Ticket number must be a number, null, or -1",
        (value) => value === null || typeof value === "number",
      ),

    vat: Yup.string()
      .nullable()
      .notRequired()
      .test("is-valid-percentage", "Vat must be a number between 0 and 100", percentageTest),

    vatOut: Yup.string()
      .nullable()
      .notRequired()
      .test("is-valid-percentage", "VatOut must be a number between 0 and 100", percentageTest),

    stockItemId: Yup.string().nullable().notRequired(),
    quantityPerSale: Yup.number()
      .nullable()
      .notRequired()
      .when("stockItemId", {
        is: (value) => !!value,
        then: (schema) => schema.required("Quantity per sale is required").moreThan(0),
      }),
    outOfStockBehavior: Yup.string()
      .nullable()
      .notRequired()
      .when("stockItemId", {
        is: (value) => !!value,
        then: (schema) => schema.oneOf(["alert", "block"]).required("Stock behavior is required"),
      }),
  });
  // =========== End of validations for product(s) ===========

  // =========== Start of validations for option(s) ===========
  const optionSchema = Yup.object().shape({
    title: Yup.string()
      .required("Title is required")
      .test("not-empty", "Title cannot be empty", (value) => value.trim() !== ""),

    category: Yup.string().typeError("Category must be a string").required("Category is required"),

    price: Yup.string()
      .required("Price is required")
      .test("not-empty", "Price cannot be empty", (value) => value.trim() !== "")
      .matches(/^-?\d*(\.\d+)?$/, "Price must be a valid number"),

    vat: Yup.string()
      .nullable()
      .notRequired()
      .test("is-valid-percentage", "Must be a number between 0 and 100", percentageTest),

    vatOut: Yup.string()
      .nullable()
      .notRequired()
      .test("is-valid-percentage", "Must be a number between 0 and 100", percentageTest),

    outOfStock: Yup.boolean().required("Out of stock is required"),

    customIcon: Yup.string().notRequired(),
    stockItemId: Yup.string().nullable().notRequired(),
    quantityPerSale: Yup.number()
      .nullable()
      .notRequired()
      .when("stockItemId", {
        is: (value) => !!value,
        then: (schema) => schema.required("Quantity per sale is required").moreThan(0),
      }),
    outOfStockBehavior: Yup.string()
      .nullable()
      .notRequired()
      .when("stockItemId", {
        is: (value) => !!value,
        then: (schema) => schema.oneOf(["alert", "block"]).required("Stock behavior is required"),
      }),
  });
  // =========== End of validations for option(s) ===========

  const normalizeNumbersRows = (rows, filename) => {
    if (!filename.toLowerCase().endsWith(".numbers")) return rows;
    // The Numbers decimal decoder can produce artifacts such as 6.000000000000001.
    // Preserve meaningful precision while restoring exact VAT and quantity values.
    return rows.map((row) => row.map((value) =>
      typeof value === "number" && Number.isFinite(value)
        ? Number(value.toPrecision(15))
        : value,
    ));
  };

  function importXlsx(event) {
    const file = event.target.files?.[0];
    if (!file || productImportRunning.current || !currentOrgId) return;
    productImportRunning.current = true;
    setLoader(true);
    setErrorText(null);
    if (fileInputRef.current) fileInputRef.current.value = null;
    const importOrgId = currentOrgId;
    const replacementIds = filteredProducts.map((product) => product.id);

    const runImport = async () => {
      try {
        const fileData = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = () => reject(reader.error || new Error(t("product_import_read_error")));
          reader.onabort = () => reject(new Error(t("product_import_read_error")));
          reader.readAsArrayBuffer(file);
        });
        const workbook = XLSX.read(new Uint8Array(fileData), { type: "array" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        if (!worksheet) throw new Error(t("change_title_warning"));
        const data = normalizeNumbersRows(XLSX.utils.sheet_to_json(worksheet, { header: 1 }), file.name);
        const colNames = data[0];
        if (!colNames || colNames.length !== excelHeaders.length ||
            !colNames.every((header, index) => header === excelHeaders[index])) {
          throw new Error(t("change_title_warning"));
        }
        const rows = transformDataOnImport(data, colNames).map((row) => {
          row.ticketNumber = row.ticketNumber ?? 0;
          row.secondTicketNumber = row.secondTicketNumber ?? 0;
          row.availableQuantity = row.availableQuantity === false || String(row.availableQuantity).toLowerCase() === "false"
            ? false : Number(row.availableQuantity ?? 0);
          return row;
        });
        // Fetch current records rather than relying on a category filter or stale state.
        const [categoriesSnapshot, productsSnapshot] = await Promise.all([
          getDocs(query(collection(db, "Categories"), where("orgId", "==", importOrgId))),
          getDocs(query(collection(db, "products"), where("orgId", "==", importOrgId))),
        ]);
        const categories = categoriesSnapshot.docs.map((record) => record.data());
        const currentProducts = productsSnapshot.docs.map((record) => ({ ...record.data(), id: record.id }));
        let newProductOffset = 0;
        const result = await importPosProducts({
          rows,
          products: currentProducts,
          replacementIds,
          validate: async (row) => {
            if (row.availableQuantity !== false && (!Number.isFinite(row.availableQuantity) || !Number.isInteger(row.availableQuantity))) {
              throw new Error(t("product_import_invalid_quantity"));
            }
            await productSchema.validate(row, { context: { customCategories: categories, baseCategories, optionCategories }, abortEarly: false });
          },
          createId: () => doc(collection(db, "products")).id,
          buildData: (row, existing) => {
            const category = categories.find((item) => item.title === row.category);
            const data = {
              price: Number(row.price), vat: row.vat ?? null, vatOut: row.vatOut ?? null,
              availableQuantity: row.availableQuantity, outOfStock: row.outOfStock || null,
              ticketColor: row.ticketColor || null, ticketNumber: row.ticketNumber || 0,
              secondTicketColor: row.secondTicketColor || null, secondTicketNumber: row.secondTicketNumber || 0,
              customIcon: row.customIcon || category?.customIcon || null,
              optionCategories: row.optionCategories || [], stockItemId: row.stockItemId || null,
              quantityPerSale: row.quantityPerSale ?? null, outOfStockBehavior: row.outOfStockBehavior || null,
            };
            return existing ? data : {
              ...data, title: row.title, category: row.category,
              uid: userStore?.users.uid || null, orgId: importOrgId,
              productOrder: currentProducts.length + newProductOffset++,
            };
          },
          commit: async (operations) => {
            const batch = writeBatch(db);
            operations.forEach(({ type, id, data }) => {
              const ref = doc(db, "products", id);
              if (type === "delete") batch.delete(ref);
              else if (type === "update") batch.update(ref, data);
              else batch.set(ref, data);
            });
            await batch.commit();
          },
        });
        alert.show(t("product_import_success", { updated: result.update, created: result.set, deleted: result.delete }), { type: "success" });
      } catch (error) {
        if (error.phase === "validation") {
          setErrorText(t("product_import_validation_error") + "\n" + error.issues.map((issue) =>
            t("product_import_row_error", { line: issue.line, title: issue.title, message: issue.message }),
          ).join("\n"));
        } else if (error.phase === "write") {
          setErrorText(t("product_import_write_error", { updated: error.completed.update, created: error.completed.set, deleted: error.completed.delete, message: error.message }));
        } else {
          setErrorText(error.message || t("product_import_read_error"));
        }
      } finally {
        productImportRunning.current = false;
        setLoader(false);
        getOrgProduct().catch((error) => console.error("Failed to refresh products after import", error));
      }
    };
    runImport();
  }

  function exportOptionsCsv() {
    const merged = {};

    optionProducts.forEach((item) => {
      const key = `${item.title}-${item.category}`;

      if (!merged[key]) {
        merged[key] = { ...item };
      } else {
        merged[key].availableQuantity += item.availableQuantity || 0;
      }
    });

    const formattedProducts = Object.keys(merged).map((key) => {
      const item = merged[key];
      return {
        title: item.title,
        category: item.category,
        price: item.price,
        vat: item?.vat,
        vatOut: item?.vatOut,
        availableQuantity: item?.availableQuantity,
        outOfStock: item.outOfStock ? "Yes" : "No",
        customIcon: item?.customIcon,
        stockItemId: item?.stockItemId ?? "",
        quantityPerSale: item?.quantityPerSale ?? "",
        outOfStockBehavior: item?.outOfStockBehavior ?? "",
      };
    });

    const worksheet = XLSX.utils.json_to_sheet([]);

    XLSX.utils.sheet_add_aoa(worksheet, [optionExcelHeaders], { origin: "A1" });
    XLSX.utils.sheet_add_json(worksheet, formattedProducts, {
      origin: "A2",
      skipHeader: true,
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "OptionProducts");

    const columnWidths = Array(optionExcelHeaders.length).fill({ wch: 25 });
    worksheet["!cols"] = columnWidths;

    XLSX.writeFile(workbook, "OptionProducts.csv", { bookType: "csv" });
  }

  function importOptionsXlsx(event) {
    const file = event.target.files[0];

    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        setLoader(true);
        let data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        data = normalizeNumbersRows(XLSX.utils.sheet_to_json(worksheet, { header: 1 }), file.name);
        const colNames = data[0];

        if (
          colNames.length !== optionExcelHeaders.length ||
          !colNames.every((header, index) => header === optionExcelHeaders[index])
        ) {
          setErrorText(t("change_title_warning"));
          setLoader(false);
          return;
        }

        let transformedData = transformDataOnImport(data, colNames);

        for (const product of optionProducts) {
          const toDelete = !transformedData.some(
            (newProduct) =>
              newProduct.title === product.title && newProduct.category === product.category,
          );
          if (toDelete) await handleDeleteOptionProduct(product.id);
        }

        for (const item of transformedData) {
          try {
            await optionSchema.validate(item, {
              context: {
                optionCategories,
                customCategories,
                baseCategories,
              },
              abortEarly: false,
            });

            const initialValue = optionProducts.find(
              (filteredItem) =>
                filteredItem.title === item.title && filteredItem.category === item.category,
            );

            if (initialValue) {
              if (Object.keys(item).some((key) => item[key] !== initialValue[key])) {
                await handleUpdateOptionFunction(item, initialValue.id);
              }
            } else {
              await handleAddOptionProduct(item);
            }
          } catch (validationError) {
            setErrorText(validationError.errors.join("\n"));
            setLoader(false);
            return;
          }
        }

        getOptionProducts();
        setLoader(false);
      };

      if (optionFileInputRef.current) optionFileInputRef.current.value = null;
      reader.readAsArrayBuffer(file);
    }
  }

  const handleCloseDeleteModal = () => setShowDelete(false);
  const handleOpenDeleteModal = () => setShowDelete(true);
  const showSidePanel = Boolean(isManageOrgOpen || addNew);

  return (
    <>
      <div className="container-fluid pos">
        <div className="row mx-0 align-items-start">
          <div className={showSidePanel ? "col-12 col-xl-8" : "col-12"}>
            <div className="card themeCard">
              <div className="card-header border-0 p-sm-20px pb-0 pb-sm-0">
                <div className="d-flex justify-content-between align-items-center pos-main-header">
                  <h5 className="m-0 tableHeading">{`POS: ${selectedOrg?.organization || ""}`}</h5>
                  <button
                    type="button"
                    className="themeBtn themeBtnSecondary m-0 text-capitalize pos-side-panel-toggle pos-side-panel-toggle-main"
                    onClick={() => setIsManageOrgOpen((prev) => !prev)}
                    aria-expanded={isManageOrgOpen}
                  >
                    <span className="pos-side-panel-toggle-label">
                      {t("advanced_configuration")}
                    </span>
                    <span
                      className={`pos-side-panel-toggle-icon ${isManageOrgOpen ? "is-open" : ""}`}
                      aria-hidden="true"
                    />
                    <span className="visually-hidden">
                      {isManageOrgOpen
                        ? t("close_advanced_configuration")
                        : t("open_advanced_configuration")}
                    </span>
                  </button>
                </div>
                <div className="pos-section-block">
                  <div className="d-flex gap-6 justify-content-between align-items-center py-4 pos-section-header">
                    <h6 className="m-0 pos-section-title">{t("your_categories")}</h6>
                    <button
                      className="themeBtn py-1 min-h-40 m-0 text-capitalize min-w-131px"
                      type="button"
                      onClick={() => {
                        setAddNew("category");
                        setSelectedProduct();
                        setSelectedCategory("all");
                      }}
                    >
                      <Plus /> {t("add_category")}
                    </button>
                  </div>
                  <div className="pos-filter-wrap">
                    <CategoryFilter
                      filters={[
                        { id: "All", label: t("all"), icon: All },
                        { id: "Beers", label: t("beers"), icon: Beer },
                        { id: "Softs", label: t("softs"), icon: Softs },
                        { id: "Food", label: t("food"), icon: Food },
                        { id: "Cocktails", label: t("cocktails"), icon: Cocktails },
                        { id: "Wine", label: t("wine"), icon: Wine },
                        { id: "Hot drinks", label: t("hot_drinks"), icon: HotDrinks },
                        { id: "Other", label: t("others"), icon: Others },
                        ...customCategories.map((cat) => ({
                          id: cat.title,
                          label: cat.title,
                          iconSrc: CategoriesArray.find((i) => i.name === cat.customIcon)?.icon,
                          onDelete: () => handleDeleteCategory(cat.id, cat.title),
                        })),
                      ]}
                      activeFilter={selectedOptionCategory ? null : selectedCategory}
                      onFilterChange={(id) => {
                        setSelectedCategory(id);
                        setSelectedOptionCategory(null);
                      }}
                    />
                  </div>
                </div>
                <div className="pos-section-block">
                  <div className="d-flex gap-6 justify-content-between align-items-start py-4 gap-2 pos-section-header">
                    <h6 className="m-0 pos-section-title">{t("your_options_categories")}</h6>
                    <div className="buttons-options-container pos-action-group">
                      <div className="pos-action-row pos-action-row-csv">
                        <input
                          type="file"
                          accept=".xlsx, .xls, .csv, .numbers"
                          ref={optionFileInputRef}
                          style={{ display: "none" }}
                          onChange={importOptionsXlsx}
                        />
                        <Button className="import-export-btn" onClick={exportOptionsCsv}>
                          {t("export_csv_options")}
                        </Button>
                        <Button
                          className="import-export-btn"
                          onClick={() => setShowOptionAlert(true)}
                        >
                          {t("import_csv_options")}
                        </Button>
                      </div>
                      <div className="pos-action-row pos-action-row-options-add">
                        <button
                          className="themeBtn btn-option py-1 min-h-40 m-0 text-capitalize min-w-131px"
                          type="button"
                          onClick={() => openOptionProductForm()}
                        >
                          <Plus /> {t("add_option")}
                        </button>
                        <button
                          type="button"
                          className="themeBtn btn-option py-1 min-h-40 m-0 text-capitalize min-w-131px"
                          onClick={() => {
                            setEditingOptionCategory(null);
                            setAddNew("optionCategory");
                            setSelectedProduct();
                          }}
                        >
                          <Plus /> {t("add_option_category")}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="pos-filter-wrap">
                    <CategoryFilter
                      filters={optionCategories.map((cat) => ({
                        id: cat.title,
                        label: cat.title,
                        iconSrc: CategoriesArray.find((i) => i.name === cat.customIcon)?.icon,
                        onDelete: () => handleDeleteOptionCategory(cat.id, cat.title),
                        editLabel: t("edit_option_category"),
                        onEdit: () => {
                          setEditingOptionCategory(cat);
                          setSelectedProduct(null);
                          setAddNew("optionCategory");
                        },
                      }))}
                      activeFilter={selectedOptionCategory}
                      onFilterChange={(id) => {
                        setSelectedOptionCategory(id);
                        setSelectedCategory("All");
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="card-body p-sm-3 pt-0 pt-sm-0">
                <div className="pos-section-block pos-products-block">
                  <div className="d-flex gap-6 justify-content-between align-items-start py-4 gap-2 pos-section-header">
                    <h6 className="m-0 pos-section-title">{t("your_products")}</h6>
                    <div className="buttons-products-container pos-action-group">
                      <div className="pos-action-row pos-action-row-csv">
                        <input
                          type="file"
                          accept=".xlsx, .xls, .csv, .numbers"
                          ref={fileInputRef}
                          style={{ display: "none" }}
                          onChange={importXlsx}
                        />
                        <Button className="import-export-btn" onClick={exportToCsv}>
                          {t("export_csv_global")}
                        </Button>
                        <Button className="import-export-btn" onClick={() => setShowAlert(true)}>
                          {t("import_csv_global")}
                        </Button>
                        <Button
                          className="import-export-btn stock-disable-btn"
                          onClick={() => setShowDisableStockConfirmation(true)}
                          disabled={!currentOrgId || disablingStock}
                        >
                          {t("disable_stock_tracking")}
                        </Button>
                      </div>
                      <div className="pos-action-row pos-action-row-menu">
                        <Button
                          className="import-export-btn"
                          onClick={exportMenuPdf}
                          disabled={!products.length || exportingMenuPdf}
                        >
                          {exportingMenuPdf ? t("menu_pdf_exporting") : t("export_pdf_menu")}
                        </Button>
                        <button
                          className="import-export-btn ai-menu-import-btn btn"
                          type="button"
                          onClick={() => setShowAiMenuImport(true)}
                        >
                          {t("ai_menu_button")}
                        </button>
                        <button
                          className="import-export-btn ai-icon-regenerate-btn btn"
                          type="button"
                          onClick={regenerateAllAiIcons}
                          disabled={regeneratingAllIcons || !currentOrgId}
                          title={t("ai_icons_regenerate_all_limit")}
                        >
                          {regeneratingAllIcons
                            ? t("ai_icons_regenerate_all_loading")
                            : t("ai_icons_regenerate_all")}
                        </button>
                      </div>
                      <div className="pos-action-row">
                        {pendingAiIcons > 0 && (
                          <span className="ai-icon-status">
                            {t("ai_icons_pending", { count: pendingAiIcons })}
                          </span>
                        )}
                        {failedAiIcons.length > 0 && (
                          <button
                            className="import-export-btn btn"
                            type="button"
                            onClick={retryFailedAiIcons}
                          >
                            {t("ai_icons_retry", { count: failedAiIcons.length })}
                          </button>
                        )}
                        <button
                          className="themeBtn py-1 min-h-40 m-0 text-capitalize min-w-131px"
                          type="button"
                          onClick={() => {
                            setAddNew("product");
                            setSelectedProduct();
                          }}
                        >
                          <Plus /> {t("add_product")}
                        </button>
                      </div>
                    </div>
                  </div>
                  {filteredProducts.length === 0 ? (
                    <div className="products-empty-container">
                      <EmptyState
                        title={
                          selectedOptionCategory ? t("opt_empty_title") : t("prod_empty_title")
                        }
                        text={selectedOptionCategory ? t("opt_empty_text") : t("prod_empty_text")}
                      />
                    </div>
                  ) : selectedOptionCategory ? (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleReorderOptionProducts}
                    >
                      <SortableContext
                        items={sortProductsForDisplay(filteredProducts, "optionOrder").map(
                          (item) => item.id,
                        )}
                        strategy={rectSortingStrategy}
                      >
                        <div className="products-grid">
                          {sortProductsForDisplay(filteredProducts, "optionOrder").map((item) => (
                            <SortableProductCard
                              key={item.id}
                              item={item}
                              selectedOptionCategory={selectedOptionCategory}
                              onOpenProduct={() => openOptionProductForm(item)}
                              t={t}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  ) : (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleReorderProducts}
                    >
                      <SortableContext
                        items={sortProductsForDisplay(filteredProducts).map((item) => item.id)}
                        strategy={rectSortingStrategy}
                      >
                        <div className="products-grid">
                          {sortProductsForDisplay(filteredProducts).map((item) => (
                            <SortableProductCard
                              key={item.id}
                              item={item}
                              selectedOptionCategory={selectedOptionCategory}
                              onOpenProduct={() => handleOpenProductForm(item)}
                              t={t}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  )}
                </div>
              </div>
            </div>
          </div>
          {showSidePanel && (
            <div className="col-12 col-xl-4">
              <div className="pos-side-panel" ref={managementSectionRef}>
                {addNew ? (
                  addNew === "product" ? (
                    <AddProduct
                      org={currentOrg}
                      selectedProduct={selectedProduct}
                      setAddNew={setAddNew}
                      customCategories={customCategories}
                      optionCategories={optionCategories}
                      handleDelete={handleOpenDeleteModal}
                    />
                  ) : addNew === "optionProduct" ? (
                    <AddOptionProduct
                      org={currentOrg}
                      selectedProduct={selectedProduct}
                      setAddNew={setAddNew}
                      optionCategories={optionCategories}
                      handleDelete={handleOpenDeleteModal}
                    />
                  ) : addNew === "optionCategory" ? (
                    <AddOptionCategory
                      key={editingOptionCategory?.id || "new-option-category"}
                      orgId={currentOrgId}
                      selectedCategory={editingOptionCategory}
                      setAddNew={setAddNew}
                    />
                  ) : (
                    <AddCategory
                      orgId={orgId[0]}
                      setAddNew={setAddNew}
                      fastpassActivated={!!currentOrg?.fastpassActivated}
                    />
                  )
                ) : (
                  <div>{selectedOrg && <ManageOrg selectedOrg={selectedOrg} />}</div>
                )}
              </div>
            </div>
          )}
          <AiMenuImport
            show={showAiMenuImport}
            onHide={() => setShowAiMenuImport(false)}
            orgId={currentOrgId}
            onCommitted={getOrgProduct}
            t={t}
            alert={alert}
          />
        </div>
      </div>
      <Modal show={showDelete} onHide={handleCloseDeleteModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("are_you_sure")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="deleteModalButtons">
            <button className="themeBtn w-100 mt-4" onClick={deleteProduct}>
              {t("delete")}
            </button>
            <OutlinedPinkButton onPressFunction={handleCloseDeleteModal}>
              {t("cancel")}
            </OutlinedPinkButton>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={showDisableStockConfirmation}
        onHide={() => !disablingStock && setShowDisableStockConfirmation(false)}
        centered
      >
        <Modal.Header closeButton={!disablingStock}>
          <Modal.Title>{t("disable_stock_tracking")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t("disable_stock_tracking_confirmation")}</p>
          <div className="deleteModalButtons">
            <button
              className="themeBtn w-100 mt-4"
              onClick={disableOrganizationStockTracking}
              disabled={disablingStock}
            >
              {disablingStock ? t("disable_stock_tracking_loading") : t("continue")}
            </button>
            <OutlinedPinkButton
              onPressFunction={() => setShowDisableStockConfirmation(false)}
              disabled={disablingStock}
            >
              {t("cancel")}
            </OutlinedPinkButton>
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={showOptionAlert} onHide={() => setShowOptionAlert(false)} centered>
        <Modal.Header closeButton>
          <div className="d-flex flex-column">
            <Modal.Title>{t("are_you_sure")}</Modal.Title>
            <p>{t("importing_warning")}</p>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="liquidityEditForm">
            <button
              className="themeBtn w-100 mt-4"
              onClick={() => {
                setShowOptionAlert(false);
                optionFileInputRef.current.click();
              }}
            >
              {t("continue")}
            </button>
            <button
              className="themeBtn btn-dark w-100 mt-4"
              onClick={() => setShowOptionAlert(false)}
            >
              {t("cancel")}
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={showAlert} onHide={() => setShowAlert(false)} centered>
        <Modal.Header closeButton>
          <div className="d-flex flex-column">
            <Modal.Title>{t("are_you_sure")}</Modal.Title>
            <p>{t("importing_warning")}</p>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="liquidityEditForm">
            <button
              className="themeBtn w-100 mt-4"
              onClick={() => {
                setShowAlert(false);
                fileInputRef.current.click();
              }}
            >
              {t("continue")}
            </button>
            <button className="themeBtn btn-dark w-100 mt-4" onClick={() => setShowAlert(false)}>
              {t("cancel")}
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={Boolean(errorText)} onHide={() => setErrorText(null)} centered scrollable>
        <Modal.Body>
          <div className="d-flex flex-column">
            <p style={{ whiteSpace: "pre-wrap" }}>{errorText}</p>
            <button className="themeBtn btn-dark w-100 mt-4" onClick={() => setErrorText(null)}>
              {t("ok")}
            </button>
          </div>
        </Modal.Body>
      </Modal>

      {loader && <Loader2 />}
    </>
  );
}

export default POS;

export { POS };
